import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import useMediaQuery from '@mui/material/useMediaQuery';
import CheckIcon from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useAppConfig } from '../../hooks/AppConfigContext';
import {
  pillSelectCompactSx, pillSelectSx,
} from '../layout/headerPill';
import { LANGUAGE_LABELS, fromGoogleCode, toGoogleCode } from '../../lib/languageLabels';
import {
  resolveDefaultLanguage, hasLanguageDefaultBeenApplied, markLanguageDefaultApplied,
} from '../../lib/browserLanguage';

declare global {
  interface Window {
    google?: { translate?: { TranslateElement: GoogleTranslateElementCtor } };
    googleTranslateElementInit?: () => void;
  }
}

interface GoogleTranslateElementCtor {
  new (config: Record<string, unknown>, elementId: string): unknown;
  InlineLayout: { SIMPLE: number };
}

const SCRIPT_ID = 'google-translate-script';
const SCRIPT_SRC = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';

// Clipped, not display:none — Google skips building the combo if the host is hidden.
const HIDDEN_SX = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

// React vs Google Translate DOM clash (facebook/react#11538). Patch once.
let domPatched = false;
const patchReactDomForGoogleTranslate = (): void => {
  if (domPatched || typeof Node !== 'function' || !Node.prototype) return;
  domPatched = true;

  const { removeChild, insertBefore } = Node.prototype;
  Node.prototype.removeChild = function patchedRemoveChild<T extends Node>(child: T): T {
    if (child.parentNode !== this) return child;
    return removeChild.call(this, child) as T;
  };
  Node.prototype.insertBefore = function patchedInsertBefore<T extends Node>(
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;
    return insertBefore.call(this, newNode, referenceNode) as T;
  };
};

// Re-assert the combo value until it sticks: the async restore-to-original resets
// it to '' and can clobber a quick re-translate (es -> en -> es). `isActive` cancels
// once a newer selection supersedes this one.
const driveGoogleCombo = (
  candidates: string[],
  isActive: () => boolean,
  attemptsLeft = 30,
): void => {
  if (!isActive() || attemptsLeft <= 0) return;
  const combo = document.querySelector<HTMLSelectElement>('select.goog-te-combo');
  if (combo) {
    const available = new Set(Array.from(combo.options).map((o) => o.value));
    const match = candidates.find((v) => available.has(v));
    if (match !== undefined && combo.value !== match) {
      combo.value = match;
      combo.dispatchEvent(new Event('change'));
    }
  }
  setTimeout(() => driveGoogleCombo(candidates, isActive, attemptsLeft - 1), 200);
};

interface GoogleTranslateProps {
  /** When true, always render in full-label mode regardless of viewport width (for use in mobile menu overlay). */
  menuMode?: boolean;
}

// Hidden Google widget driven by a MUI Select matching LanguageSelector.
const GoogleTranslate = ({ menuMode }: GoogleTranslateProps = {}) => {
  const { config } = useAppConfig();
  const { t, i18n } = useTranslation();
  const muiTheme = useTheme();
  // Tight header space on phones: collapse the dropdown to an icon-only trigger.
  // In menuMode (mobile overlay) always show full label.
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const compact = !menuMode && isSmallScreen;
  const languages = config.languagesEnabled.length ? config.languagesEnabled : ['en'];
  const includedLanguages = languages.map(toGoogleCode).join(',');
  const enabledLanguages = languages.join(',');
  const pageLanguage = languages[0];
  const googlePageLanguage = toGoogleCode(pageLanguage);

  const [current, setCurrent] = useState(pageLanguage);
  // Bumped per selection to cancel a stale re-assertion loop.
  const requestRef = useRef(0);

  useEffect(() => {
    patchReactDomForGoogleTranslate();

    window.googleTranslateElementInit = () => {
      const TranslateElement = window.google?.translate?.TranslateElement;
      if (!TranslateElement) return;
      // Default layout: SIMPLE renders a popup with no select.goog-te-combo to drive.
      // eslint-disable-next-line no-new
      new TranslateElement(
        {
          pageLanguage: googlePageLanguage,
          includedLanguages,
          autoDisplay: false,
        },
        'google_translate_element',
      );
    };

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      document.body.appendChild(script);
    } else if (window.google?.translate?.TranslateElement) {
      window.googleTranslateElementInit();
    }
  }, [includedLanguages, googlePageLanguage]);

  const handleChange = useCallback((lang: string): void => {
    setCurrent(lang);
    // i18n translates keyed chrome; the Google combo drive below handles dynamic data.
    i18n.changeLanguage(lang);
    // Page language: its code restores post-translation, '' pre-translation.
    const candidates = lang === pageLanguage ? [toGoogleCode(lang), ''] : [toGoogleCode(lang)];
    requestRef.current += 1;
    const token = requestRef.current;
    driveGoogleCombo(candidates, () => token === requestRef.current);
  }, [i18n, pageLanguage]);

  // Boot language priority: googtrans cookie, then browser default (first visit only), else org default.
  useEffect(() => {
    const enabled = enabledLanguages.split(',');
    const target = document.cookie.match(/(?:^|;\s*)googtrans=[^/]*\/[^/]*\/([^;]+)/)?.[1];

    if (target) {
      const lang = fromGoogleCode(decodeURIComponent(target));
      if (enabled.includes(lang)) {
        setCurrent(lang);
        if (i18n.language !== lang) i18n.changeLanguage(lang);
      }
      return;
    }

    if (hasLanguageDefaultBeenApplied()) {
      // Returning visitor, no cookie: keep org default.
      setCurrent(pageLanguage);
      if (i18n.language !== pageLanguage) i18n.changeLanguage(pageLanguage);
      return;
    }

    markLanguageDefaultApplied();
    const preferred = resolveDefaultLanguage(enabled);
    if (preferred !== pageLanguage && enabled.includes(preferred)) {
      // Non-base language: drive Google combo too, not just i18n.
      handleChange(preferred);
    } else {
      setCurrent(pageLanguage);
      if (i18n.language !== pageLanguage) i18n.changeLanguage(pageLanguage);
    }
  }, [enabledLanguages, pageLanguage, i18n, handleChange]);

  if (languages.length <= 1) return <Box id="google_translate_element" sx={HIDDEN_SX} />;

  const menuItems = languages.map((lng) => (
    <MenuItem key={lng} value={lng} sx={{ padding: '12px !important' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1,
      }}
      >
        <span>{LANGUAGE_LABELS[lng] || lng}</span>
        {current === lng && <CheckIcon sx={{ fontSize: 16 }} />}
      </Box>
    </MenuItem>
  ));

  if (menuMode) {
    return (
      <>
        <Box id="google_translate_element" sx={HIDDEN_SX} />
        <Select
          value={current}
          onChange={(e) => handleChange(e.target.value)}
          inputProps={{ 'aria-label': t('finder.language_selector_label') }}
          renderValue={(value) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageIcon sx={{ fontSize: 20, opacity: 0.9, flexShrink: 0 }} />
              <Box component="span">{LANGUAGE_LABELS[value as string] || value}</Box>
            </Box>
          )}
          fullWidth
          MenuProps={{
            PaperProps: {
              sx: {
                width: 'calc(100vw - 32px)',
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '0 !important',
                left: '16px !important',
                right: '16px !important',
              },
            },
          }}
          sx={{
            borderRadius: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            fontSize: '0.875rem',
            fontWeight: 500,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-select': {
              py: 0,
              px: 2,
              minHeight: '52px !important',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiSelect-icon': { color: 'text.secondary', mr: 1 },
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {menuItems}
        </Select>
      </>
    );
  }

  return (
    <>
      <Box id="google_translate_element" sx={HIDDEN_SX} />
      <Select
        value={current}
        onChange={(e) => handleChange(e.target.value)}
        inputProps={{ 'aria-label': t('finder.language_selector_label') }}
        renderValue={(value) => (
          compact ? (
            <LanguageIcon sx={{ fontSize: 18 }} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
              }}
            >
              <LanguageIcon sx={{ fontSize: 16, opacity: 0.9, flexShrink: 0 }} />
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {LANGUAGE_LABELS[value as string] || value}
              </Box>
            </Box>
          )
        )}
        sx={compact ? pillSelectCompactSx : pillSelectSx}
        size="small"
      >
        {menuItems}
      </Select>
    </>
  );
};

export default GoogleTranslate;
